import React, { useState, useEffect } from 'react';

import Editor from '@monaco-editor/react';
const API = 'https://talentflow-backend-production-09dd.up.railway.app/api';

// Global camera stream - persists across re-renders
let globalStream = null;
const attachStream = (videoEl) => {
  if (videoEl && globalStream) {
    videoEl.srcObject = globalStream;
    videoEl.muted = true;
    videoEl.playsInline = true;
    videoEl.play().catch(() => {});
  }
};

// Global timer for entire phase
function PhaseTimer({ totalSeconds, onExpire }) {
  const [time, setTime] = useState(totalSeconds);
  useEffect(() => {
    if (time <= 0) { onExpire(); return; }
    const t = setInterval(() => setTime(p => p - 1), 1000);
    return () => clearInterval(t);
  }, [time]);
  const mins = Math.floor(time / 60);
  const secs = time % 60;
  const pct = (time / totalSeconds) * 100;
  const color = time > 300 ? '#16a34a' : time > 120 ? '#f59e0b' : '#dc2626';
  return (
    <div style={{ display:'flex', alignItems:'center', gap:'8px' }}>
      <div style={{ position:'relative', width:'44px', height:'44px' }}>
        <svg width="44" height="44" style={{ transform:'rotate(-90deg)' }}>
          <circle cx="22" cy="22" r="18" fill="none" stroke="rgba(255,255,255,0.2)" strokeWidth="3"/>
          <circle cx="22" cy="22" r="18" fill="none" stroke={color} strokeWidth="3"
            strokeDasharray={`${2*Math.PI*18}`}
            strokeDashoffset={`${2*Math.PI*18*(1-pct/100)}`}
            style={{ transition:'stroke-dashoffset 1s linear' }}/>
        </svg>
        <div style={{ position:'absolute', inset:0, display:'flex', alignItems:'center', justifyContent:'center', fontSize:'9px', fontWeight:'800', color }}>
          {mins}:{secs.toString().padStart(2,'0')}
        </div>
      </div>
      <div>
        <div style={{ color, fontSize:'13px', fontWeight:'700' }}>{mins}:{secs.toString().padStart(2,'0')}</div>
        <div style={{ color:'rgba(255,255,255,0.4)', fontSize:'10px' }}>{time > 300 ? 'Phase Timer' : time > 120 ? '⚠️ Hurry!' : '🔴 Almost!'}</div>
      </div>
    </div>
  );
}

export default function Interview() {
  const params = new URLSearchParams(window.location.search);
  const candidateId = params.get('id');
  const isBench = window.location.pathname === '/bench-interview';
  const questionsUrl = isBench ? `${API}/interview/bench-questions/${candidateId}` : `${API}/interview/questions/${candidateId}`;
  const submitUrl = isBench ? `${API}/interview/bench-submit/${candidateId}` : `${API}/interview/submit/${candidateId}`;

  // Phase control
  const [phase, setPhase] = useState('loading'); // loading, intro, text, transition, video, submitting, result
  const [candidateInfo, setCandidateInfo] = useState(null);
  const [error, setError] = useState('');

  // Text interview
  const [textQuestions, setTextQuestions] = useState([]);
  const [current, setCurrent] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [currentAnswer, setCurrentAnswer] = useState('');
  const [textScores, setTextScores] = useState(null);

  // Video interview
  const [videoQuestions, setVideoQuestions] = useState([]);
  const [videoCurrent, setVideoCurrent] = useState(0);
  const [videoAnswers, setVideoAnswers] = useState([]);
  const [currentVideoAnswer, setCurrentVideoAnswer] = useState('');
  const [isRecording, setIsRecording] = useState(false);
  const [finalResult, setFinalResult] = useState(null);

  const videoRef = React.useRef(null);
  const streamRef = React.useRef(null);
  const confidenceRef = React.useRef({ pauseCount:0, fillerCount:0, wordCount:0, startTime:null, lastSpeechTime:null, pauseThreshold:2000 });
  const [confidenceData, setConfidenceData] = React.useState({ pauseCount:0, fillerCount:0, wordCount:0 });
  const recognitionRef = React.useRef(null);

  useEffect(() => {
    if (!candidateId) { setError('Invalid interview link'); setPhase('error'); return; }
    loadTextQuestions();
  }, []);

  const loadTextQuestions = async () => {
    try {
      const r = await fetch(questionsUrl);
      const d = await r.json();
      if (d.success) { setTextQuestions(d.questions); setCandidateInfo(d.candidateInfo); setPhase('intro'); }
      else { setError(d.message); setPhase('error'); }
    } catch { setError('Failed to load.'); setPhase('error'); }
  };

  const loadVideoQuestions = async () => {
    try {
      const r = await fetch(`${API}/interview/video-questions/${candidateId}`);
      const d = await r.json();
      if (d.success) { setVideoQuestions(d.questions); }
    } catch {}
  };

  // Text answer submit
  const goNext = async (ans) => {
    const q = textQuestions[current];
    const newAnswers = [...answers, { question: q.question, answer: ans || '[Skipped]', category: q.category, type: q.type, level: q.level, correct: q.correct }];
    setAnswers(newAnswers);
    setCurrentAnswer('');

    if (current < textQuestions.length - 1) {
      setCurrent(current + 1);
    } else {
      // Submit text interview
      setPhase('submitting');
      try {
        const r = await fetch(submitUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ answers: newAnswers })
        });
        const d = await r.json();
        if (d.success) {
          setTextScores(d.scores);
          await loadVideoQuestions();
          setPhase('transition');
        }
      } catch { setError('Submission failed.'); setPhase('error'); }
    }
  };

  // Video functions
  const speakQuestion = React.useCallback((text) => {
    if (!window.speechSynthesis) return;
    window.speechSynthesis.cancel();
    const doSpeak = () => {
      const utt = new SpeechSynthesisUtterance(text);
      utt.rate = 0.85; utt.pitch = 1; utt.volume = 1;
      const voices = window.speechSynthesis.getVoices();
      const eng = voices.find(v => v.lang.startsWith('en'));
      if (eng) utt.voice = eng;
      window.speechSynthesis.speak(utt);
    };
    if (window.speechSynthesis.getVoices().length > 0) setTimeout(doSpeak, 600);
    else window.speechSynthesis.onvoiceschanged = () => setTimeout(doSpeak, 600);
  }, []);

  const stopVideo = React.useCallback(() => {
    if (recognitionRef.current) { try { recognitionRef.current.stop(); } catch(e) {} }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
    if (videoRef.current) { videoRef.current.srcObject = null; }
    setIsRecording(false);
    if (window.speechSynthesis) window.speechSynthesis.cancel();
  }, []);

  const startVideo = React.useCallback(async (questionText) => {
    try {
      // Stop existing stream first
      if (globalStream) {
        globalStream.getTracks().forEach(t => t.stop());
        globalStream = null;
      }
      if (recognitionRef.current) { try { recognitionRef.current.stop(); } catch(e) {} }
      
      await new Promise(r => setTimeout(r, 500));
      
      const constraints = {
        video: { width: { ideal: 640 }, height: { ideal: 480 }, facingMode: 'user' },
        audio: true
      };
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      globalStream = stream; streamRef.current = stream;
      
      // Attach stream to video element
      await new Promise(r => setTimeout(r, 300));
      const videoEl = videoRef.current || document.getElementById('skillora-video');
      if (videoEl) {
        videoEl.srcObject = null;
        await new Promise(r => setTimeout(r, 100));
        videoEl.srcObject = stream;
        videoEl.muted = true;
        videoEl.playsInline = true;
        videoEl.autoplay = true;
        videoEl.volume = 0;
        await new Promise(r => setTimeout(r, 200));
        try {
          await videoEl.play();
        } catch(e) {
          videoEl.oncanplay = () => videoEl.play().catch(()=>{});
        }
        setIsRecording(true);
      }
      setTimeout(() => speakQuestion(questionText), 1500);
      if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
        const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
        const rec = new SR();
        rec.continuous = true; rec.interimResults = true; rec.lang = 'en-US';
        rec.onstart = () => {
          // recording started
          confidenceRef.current.startTime = Date.now();
          confidenceRef.current.lastSpeechTime = Date.now();
          confidenceRef.current.pauseCount = 0;
          confidenceRef.current.fillerCount = 0;
          confidenceRef.current.wordCount = 0;
        };
        rec.onresult = (e) => {
          let final = '', interim = '';
          for (let i = 0; i < e.results.length; i++) {
            if (e.results[i].isFinal) {
              const transcript = e.results[i][0].transcript;
              final += transcript + ' ';
              
              // Count filler words
              const fillers = (transcript.match(/\b(um|uh|like|you know|basically|actually|literally|so|right|okay|hmm)\b/gi) || []).length;
              confidenceRef.current.fillerCount += fillers;
              
              // Count words
              const words = transcript.trim().split(/\s+/).length;
              confidenceRef.current.wordCount += words;
              
              // Detect pause (time since last speech)
              const now = Date.now();
              if (confidenceRef.current.lastSpeechTime && (now - confidenceRef.current.lastSpeechTime) > confidenceRef.current.pauseThreshold) {
                confidenceRef.current.pauseCount++;
              }
              confidenceRef.current.lastSpeechTime = now;
            } else {
              interim += e.results[i][0].transcript;
            }
          }
          setCurrentVideoAnswer(final + interim);
          setConfidenceData({...confidenceRef.current});
        };
        rec.onerror = (e) => { 
          console.log('rec error:', e.error);
          if (e.error === 'not-allowed') {
            console.log('Mic permission denied - continuing without voice');
          } else {
            setTimeout(() => { try { rec.start(); } catch(err) {} }, 1000);
          }
        };
        rec.onend = () => { 
          if (isRecording) {
            setTimeout(() => { try { rec.start(); } catch(err) {} }, 500);
          }
        };
        rec.start();
        recognitionRef.current = rec;
      }
    } catch { alert('Camera/Mic access required!'); }
  }, [speakQuestion, stopVideo]);

  const videoStarted = React.useRef(false);
  const [code, setCode] = React.useState('// Write your code here\n\n');
  const [codeLang, setCodeLang] = React.useState('javascript');
  const [showCoding, setShowCoding] = React.useState(false);

  const restartRecognition = React.useCallback((questionText) => {
    // Stop current recognition
    if (recognitionRef.current) { try { recognitionRef.current.stop(); recognitionRef.current = null; } catch(e) {} }
    
    // Speak question first, then start recognition after speaking is done
    speakQuestion(questionText);
    
    // Wait for speech to finish (estimate: 3-5 seconds) before starting mic
    const speakDuration = Math.max(3000, questionText.length * 60);
    setTimeout(() => {
      if (!('webkitSpeechRecognition' in window || 'SpeechRecognition' in window)) return;
      try {
        const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
        const rec = new SR();
        rec.continuous = true; rec.interimResults = true; rec.lang = 'en-US';
        rec.onresult = (e) => {
          let transcript = '';
          for (let i = e.resultIndex; i < e.results.length; i++) {
            transcript += e.results[i][0].transcript;
          }
          setCurrentVideoAnswer(transcript);
          confidenceRef.current.wordCount = transcript.split(' ').filter(Boolean).length;
          setConfidenceData(prev => ({...prev, wordCount: confidenceRef.current.wordCount}));
        };
        rec.onerror = (e) => { console.log('Recognition error:', e.error); };
        rec.onend = () => {
          if (recognitionRef.current === rec) {
            try { rec.start(); } catch(e) {}
          }
        };
        rec.start();
        recognitionRef.current = rec;
        setIsRecording(true);
      } catch(e) { console.log('Recognition error:', e); }
    }, speakDuration);
  }, [speakQuestion]);

  useEffect(() => {
    let timer;
    if (phase === 'video' && videoQuestions.length > 0) {
      const q = videoQuestions[videoCurrent];
      if (q) {
        if (!videoStarted.current) {
          videoStarted.current = true;
          timer = setTimeout(() => {
            startVideo(`Question ${videoCurrent + 1}. ${q.question}`);
          }, 1500);
        } else {
          // Restart camera + recognition for each new question
          timer = setTimeout(() => {
            startVideo(`Question ${videoCurrent + 1}. ${q.question}`);
          }, 500);
        }
      }
    }
    return () => { clearTimeout(timer); };
  }, [phase, videoCurrent, videoQuestions]);

  const goNextVideo = async (ans) => {
    // Stop recognition but KEEP camera stream running
    if (recognitionRef.current) { try { recognitionRef.current.stop(); recognitionRef.current = null; } catch(e) {} }
    const q = videoQuestions[videoCurrent];
    const timeTaken = confidenceRef.current.startTime ? Math.round((Date.now() - confidenceRef.current.startTime) / 1000) : 0;
    const wordsPerMin = timeTaken > 0 ? Math.round((confidenceRef.current.wordCount / timeTaken) * 60) : 0;
    const newVideoAnswers = [...videoAnswers, { 
      question: q.question, 
      answer: ans || '[No answer]',
      metadata: {
        wordCount: confidenceRef.current.wordCount,
        pauseCount: confidenceRef.current.pauseCount,
        fillerCount: confidenceRef.current.fillerCount,
        timeTaken,
        wordsPerMin,
      }
    }];
    setVideoAnswers(newVideoAnswers);
    setCurrentVideoAnswer('');
    stopVideo();

    if (videoCurrent < videoQuestions.length - 1) {
      setVideoCurrent(videoCurrent + 1);
    } else {
      // Submit video interview
      setPhase('submitting');
      try {
        const r = await fetch(`${API}/interview/submit-video/${candidateId}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ videoAnswers: newVideoAnswers })
        });
        const d = await r.json();
        if (d.success) { setFinalResult({ ...textScores, ...d.videoScores, finalScore: d.finalScore }); setPhase('result'); }
        else { setError(d.message); setPhase('error'); }
      } catch { setError('Submission failed.'); setPhase('error'); }
    }
  };

  const base = { fontFamily:'-apple-system,BlinkMacSystemFont,sans-serif', minHeight:'100vh', background:'linear-gradient(135deg,#0f172a,#1e1b4b)' };
  const scoreColor = (s) => !s ? '#94a3b8' : s>=8 ? '#16a34a' : s>=6 ? '#f59e0b' : '#dc2626';
  const scoreLabel = (s) => !s ? 'N/A' : s>=8 ? 'Excellent' : s>=6 ? 'Good' : 'Needs Work';

  if (phase === 'loading') return (
    <div style={{ ...base, display:'flex', alignItems:'center', justifyContent:'center' }}>
      <div style={{ textAlign:'center' }}>
        <div style={{ width:'52px', height:'52px', border:'3px solid rgba(99,102,241,0.3)', borderTop:'3px solid #6366f1', borderRadius:'50%', animation:'spin 1s linear infinite', margin:'0 auto 16px' }}/>
        <p style={{ color:'rgba(255,255,255,0.5)', fontSize:'14px' }}>Preparing your interview...</p>
        <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
      </div>
    </div>
  );

  if (phase === 'error') return (
    <div style={{ ...base, display:'flex', alignItems:'center', justifyContent:'center', padding:'20px' }}>
      <div style={{ background:'white', borderRadius:'20px', padding:'48px', maxWidth:'420px', width:'100%', textAlign:'center' }}>
        <div style={{ fontSize:'56px', marginBottom:'16px' }}>❌</div>
        <h2 style={{ color:'#1e293b', margin:'0 0 8px' }}>Something went wrong</h2>
        <p style={{ color:'#64748b', fontSize:'14px', margin:'0 0 24px' }}>{error}</p>
        <button onClick={loadTextQuestions} style={{ padding:'12px 28px', background:'linear-gradient(135deg,#6366f1,#8b5cf6)', color:'white', border:'none', borderRadius:'10px', cursor:'pointer', fontFamily:'inherit', fontWeight:'700' }}>Try Again</button>
      </div>
    </div>
  );

  if (phase === 'intro') return (
    <div style={{ ...base, display:'flex', alignItems:'center', justifyContent:'center', padding:'20px' }}>
      <div style={{ background:'white', borderRadius:'24px', padding:'48px', maxWidth:'620px', width:'100%', boxShadow:'0 40px 80px rgba(0,0,0,0.4)' }}>
        <div style={{ display:'flex', justifyContent:'center', marginBottom:'24px' }}>
          <div style={{ width:'72px', height:'72px', background:'linear-gradient(135deg,#6366f1,#8b5cf6)', borderRadius:'20px', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'32px' }}>⚡</div>
        </div>
        <h2 style={{ color:'#1e293b', fontSize:'26px', fontWeight:'900', margin:'0 0 6px', textAlign:'center' }}>Skillora AI Interview</h2>
        <p style={{ color:'#64748b', fontSize:'14px', margin:'0 0 32px', textAlign:'center' }}>Hello <strong>{candidateInfo?.name}</strong>! Position: <strong>{candidateInfo?.jobTitle}</strong></p>

        {/* Two phases */}
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'16px', marginBottom:'28px' }}>
          <div style={{ background:'#f5f3ff', borderRadius:'16px', padding:'20px', border:'1px solid #c4b5fd', textAlign:'center' }}>
            <div style={{ fontSize:'32px', marginBottom:'8px' }}>✍️</div>
            <div style={{ color:'#6366f1', fontSize:'20px', fontWeight:'800' }}>Phase 1</div>
            <div style={{ color:'#1e293b', fontSize:'14px', fontWeight:'700', margin:'4px 0' }}>Text Interview</div>
            <div style={{ color:'#64748b', fontSize:'12px' }}>30 Questions • 15 Minutes</div>
            <div style={{ color:'#6366f1', fontSize:'11px', marginTop:'6px' }}>Technical + Written + English</div>
          </div>
          <div style={{ background:'#0f172a', borderRadius:'16px', padding:'20px', border:'1px solid rgba(99,102,241,0.3)', textAlign:'center' }}>
            <div style={{ fontSize:'32px', marginBottom:'8px' }}>🎥</div>
            <div style={{ color:'#818cf8', fontSize:'20px', fontWeight:'800' }}>Phase 2</div>
            <div style={{ color:'white', fontSize:'14px', fontWeight:'700', margin:'4px 0' }}>Video Interview</div>
            <div style={{ color:'rgba(255,255,255,0.5)', fontSize:'12px' }}>15 Questions • 15 Minutes</div>
            <div style={{ color:'#818cf8', fontSize:'11px', marginTop:'6px' }}>AI Voice + Camera</div>
          </div>
        </div>

        <div style={{ background:'#f8fafc', borderRadius:'12px', padding:'18px', marginBottom:'28px', border:'1px solid #e2e8f0' }}>
          <h4 style={{ color:'#1e293b', margin:'0 0 12px', fontSize:'14px', fontWeight:'700' }}>📋 Instructions</h4>
          {[
            'Phase 1: Answer 30 text questions within 15 minutes total',
            'Phase 2: Answer 15 video questions via camera within 15 minutes',
            'AI will evaluate both phases and generate combined report',
            'Ensure camera and microphone are ready before starting',
            'Cannot go back to previous questions',
          ].map((item, i) => (
            <div key={i} style={{ display:'flex', alignItems:'flex-start', gap:'8px', marginBottom:'8px' }}>
              <span style={{ width:'18px', height:'18px', borderRadius:'50%', background:'#ede9fe', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'10px', color:'#6366f1', fontWeight:'800', flexShrink:0 }}>{i+1}</span>
              <span style={{ color:'#64748b', fontSize:'13px', lineHeight:'1.5' }}>{item}</span>
            </div>
          ))}
        </div>

        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'12px' }}>
          <button onClick={() => setPhase('text')}
            style={{ padding:'16px', background:'linear-gradient(135deg,#6366f1,#8b5cf6)', color:'white', border:'none', borderRadius:'12px', cursor:'pointer', fontSize:'16px', fontWeight:'800', fontFamily:'inherit', boxShadow:'0 0 30px rgba(99,102,241,0.4)' }}>
            🚀 Start Interview
          </button>
          <button onClick={() => { loadVideoQuestions().then(()=>setPhase('video')); }}
            style={{ padding:'16px', background:'rgba(255,255,255,0.05)', color:'rgba(255,255,255,0.5)', border:'1px solid rgba(255,255,255,0.1)', borderRadius:'12px', cursor:'pointer', fontSize:'13px', fontWeight:'600', fontFamily:'inherit' }}>
            🎥 Test Video Only
          </button>
        </div>
      </div>
    </div>
  );

  if (phase === 'text') {
    const q = textQuestions[current];
    if (!q) return null;
    const progress = (current / textQuestions.length) * 100;
    const typeColors = { technical:{ bg:'#ede9fe', color:'#6366f1' }, written:{ bg:'#dcfce7', color:'#16a34a' }, english:{ bg:'#fef9c3', color:'#ca8a04' } };
    const tc = typeColors[q.category] || typeColors.technical;

    return (
      <div style={{ ...base, padding:'20px', display:'flex', flexDirection:'column', alignItems:'center' }}>
        <div style={{ width:'100%', maxWidth:'740px' }}>
          {/* Header */}
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'16px' }}>
            <div style={{ display:'flex', alignItems:'center', gap:'10px' }}>
              <div style={{ width:'32px', height:'32px', background:'linear-gradient(135deg,#6366f1,#8b5cf6)', borderRadius:'8px', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'16px' }}>⚡</div>
              <div>
                <div style={{ color:'white', fontWeight:'800', fontSize:'14px' }}>Phase 1 — Text Interview</div>
                <div style={{ color:'rgba(255,255,255,0.4)', fontSize:'11px' }}>Q {current+1} of {textQuestions.length}</div>
              </div>
            </div>
            <PhaseTimer totalSeconds={900} onExpire={() => goNext(currentAnswer)} />
          </div>

          {/* Progress */}
          <div style={{ background:'rgba(255,255,255,0.08)', borderRadius:'20px', height:'4px', marginBottom:'20px' }}>
            <div style={{ background:'linear-gradient(135deg,#6366f1,#8b5cf6)', height:'100%', width:`${progress}%`, borderRadius:'20px', transition:'width 0.4s' }}/>
          </div>

          {/* Question Card */}
          <div style={{ background:'white', borderRadius:'20px', padding:'32px', boxShadow:'0 20px 60px rgba(0,0,0,0.3)' }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'20px', flexWrap:'wrap', gap:'8px' }}>
              <div style={{ display:'flex', gap:'8px' }}>
                <span style={{ padding:'4px 10px', background:tc.bg, color:tc.color, borderRadius:'20px', fontSize:'11px', fontWeight:'700' }}>{q.category?.toUpperCase()}</span>
                <span style={{ padding:'4px 10px', background:'#f8fafc', color:'#64748b', borderRadius:'20px', fontSize:'11px', fontWeight:'600', border:'1px solid #e2e8f0' }}>{q.level}</span>
                {q.optional && <span style={{ padding:'4px 10px', background:'#fffbeb', color:'#ca8a04', borderRadius:'20px', fontSize:'11px' }}>Optional</span>}
              </div>
            </div>

            <h3 style={{ color:'#1e293b', fontSize:'18px', fontWeight:'700', lineHeight:'1.6', margin:'0 0 24px' }}>Q{current+1}. {q.question}</h3>

            {/* MCQ Options */}
            {q.type === 'mcq' && q.options ? (
              <div style={{ display:'grid', gap:'10px', marginBottom:'8px' }}>
                {q.options.map((opt, i) => (
                  <div key={i} onClick={() => setCurrentAnswer(opt.charAt(0))}
                    style={{ padding:'14px 18px', borderRadius:'10px', cursor:'pointer', fontSize:'14px', fontWeight:'500',
                      background: currentAnswer === opt.charAt(0) ? 'linear-gradient(135deg,#6366f1,#8b5cf6)' : '#f8fafc',
                      color: currentAnswer === opt.charAt(0) ? 'white' : '#1e293b',
                      border: currentAnswer === opt.charAt(0) ? 'none' : '2px solid #e2e8f0',
                      transition:'all 0.15s' }}>
                    {opt}
                  </div>
                ))}
              </div>
            ) : (
              <textarea value={currentAnswer} onChange={e => setCurrentAnswer(e.target.value)}
                placeholder="Type your answer here..."
                style={{ width:'100%', padding:'16px', border:'2px solid #e2e8f0', borderRadius:'12px', fontSize:'14px', lineHeight:'1.7', resize:'vertical', minHeight:'140px', outline:'none', fontFamily:'inherit', boxSizing:'border-box', color:'#1e293b' }}
                onFocus={e => e.target.style.borderColor='#6366f1'}
                onBlur={e => e.target.style.borderColor='#e2e8f0'}
                autoFocus/>
            )}

            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginTop:'20px' }}>
              <span style={{ color:'#94a3b8', fontSize:'12px' }}>{currentAnswer.length} chars</span>
              <div style={{ display:'flex', gap:'8px' }}>
                <button onClick={() => {
                  const remaining = textQuestions.slice(current);
                  const allAnswers = [...answers, ...remaining.map(q => ({ question:q.question, answer:'[Skipped]', category:q.category, type:q.type, level:q.level, correct:q.correct }))];
                  setAnswers(allAnswers);
                  setPhase('submitting');
                  fetch(submitUrl, {
                    method:'POST', headers:{'Content-Type':'application/json'},
                    body: JSON.stringify({ answers: allAnswers })
                  }).then(r=>r.json()).then(d=>{ if(d.success){ setTextScores(d.scores); loadVideoQuestions().then(()=>setPhase('transition')); } });
                }} style={{ padding:'10px 18px', background:'rgba(99,102,241,0.1)', color:'#6366f1', border:'1px solid rgba(99,102,241,0.3)', borderRadius:'8px', cursor:'pointer', fontSize:'12px', fontFamily:'inherit', fontWeight:'600' }}>⏭ Skip to Video</button>
                {q.optional && (
                  <button onClick={() => goNext('')} style={{ padding:'10px 18px', background:'#f8fafc', color:'#64748b', border:'1px solid #e2e8f0', borderRadius:'8px', cursor:'pointer', fontSize:'13px', fontFamily:'inherit', fontWeight:'600' }}>Skip</button>
                )}
                <button onClick={() => goNext(currentAnswer)} disabled={!currentAnswer.trim() && !q.optional}
                  style={{ padding:'12px 28px', background:currentAnswer.trim()?'linear-gradient(135deg,#6366f1,#8b5cf6)':'#f1f5f9', color:currentAnswer.trim()?'white':'#94a3b8', border:'none', borderRadius:'10px', cursor:currentAnswer.trim()?'pointer':'not-allowed', fontSize:'14px', fontWeight:'700', fontFamily:'inherit' }}>
                  {current < textQuestions.length-1 ? 'Next →' : '✓ Complete Phase 1'}
                </button>
              </div>
            </div>
          </div>

          {/* Dot map */}
          <div style={{ display:'flex', gap:'3px', flexWrap:'wrap', marginTop:'16px', justifyContent:'center' }}>
            {textQuestions.map((_,i) => (
              <div key={i} style={{ width:'8px', height:'8px', borderRadius:'50%', background: i<current?'#6366f1':i===current?'white':'rgba(255,255,255,0.2)' }}/>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (phase === 'transition') return (
    <div style={{ ...base, display:'flex', alignItems:'center', justifyContent:'center', padding:'20px' }}>
      <div style={{ background:'white', borderRadius:'24px', padding:'48px', maxWidth:'520px', width:'100%', textAlign:'center', boxShadow:'0 40px 80px rgba(0,0,0,0.4)' }}>
        <div style={{ fontSize:'56px', marginBottom:'16px' }}>🎉</div>
        <h2 style={{ color:'#1e293b', fontSize:'24px', fontWeight:'900', margin:'0 0 8px' }}>Phase 1 Complete!</h2>
        <p style={{ color:'#64748b', fontSize:'14px', margin:'0 0 24px' }}>Text interview done. Now prepare for video interview.</p>

        {textScores && (
          <div style={{ background:'#f5f3ff', borderRadius:'12px', padding:'16px', marginBottom:'24px', border:'1px solid #c4b5fd' }}>
            <div style={{ color:'#6366f1', fontSize:'28px', fontWeight:'900' }}>{textScores.overallScore}/10</div>
            <div style={{ color:'#64748b', fontSize:'13px' }}>Phase 1 Score</div>
          </div>
        )}

        <div style={{ background:'#0f172a', borderRadius:'16px', padding:'20px', marginBottom:'28px', textAlign:'left' }}>
          <h4 style={{ color:'white', fontSize:'14px', fontWeight:'700', margin:'0 0 12px' }}>📋 Phase 2 — Video Interview</h4>
          {['Allow camera and microphone access','AI will ask 15 questions via voice','Speak your answers clearly','15 minutes total time limit'].map((tip,i) => (
            <div key={i} style={{ display:'flex', gap:'8px', marginBottom:'8px' }}>
              <span style={{ color:'#4ade80', fontSize:'12px' }}>✓</span>
              <span style={{ color:'rgba(255,255,255,0.6)', fontSize:'13px' }}>{tip}</span>
            </div>
          ))}
        </div>

        <button onClick={() => setPhase('video')}
          style={{ width:'100%', padding:'16px', background:'linear-gradient(135deg,#0f172a,#1e1b4b)', color:'white', border:'2px solid rgba(99,102,241,0.5)', borderRadius:'12px', cursor:'pointer', fontSize:'16px', fontWeight:'800', fontFamily:'inherit' }}>
          🎥 Start Video Interview
        </button>
      </div>
    </div>
  );

  if (phase === 'video') {
    const q = videoQuestions[videoCurrent];
    if (!q) return null;
    const progress = (videoCurrent / videoQuestions.length) * 100;

    return (
      <div style={{ fontFamily:'-apple-system,BlinkMacSystemFont,sans-serif', minHeight:'100vh', background:'#0a0f1e', display:'flex', flexDirection:'column' }}>
        {/* Header */}
        <div style={{ background:'linear-gradient(135deg,#6366f1,#8b5cf6)', padding:'12px 24px', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
          <div style={{ display:'flex', alignItems:'center', gap:'10px' }}>
            <span style={{ fontSize:'16px' }}>⚡</span>
            <div>
              <div style={{ color:'white', fontWeight:'800', fontSize:'14px' }}>Phase 2 — Video Interview</div>
              <div style={{ color:'rgba(255,255,255,0.7)', fontSize:'11px' }}>Q {videoCurrent+1} of {videoQuestions.length}</div>
            </div>
            {isRecording && <span style={{ background:'rgba(239,68,68,0.3)', color:'#fca5a5', padding:'3px 10px', borderRadius:'20px', fontSize:'11px', fontWeight:'700' }}>🔴 REC</span>}
          </div>
          <PhaseTimer totalSeconds={900} onExpire={() => goNextVideo(currentVideoAnswer)} />
        </div>

        {/* Progress */}
        <div style={{ background:'rgba(255,255,255,0.05)', height:'3px' }}>
          <div style={{ background:'linear-gradient(135deg,#6366f1,#8b5cf6)', height:'100%', width:`${progress}%`, transition:'width 0.4s' }}/>
        </div>

        <div style={{ flex:1, display:'grid', gridTemplateColumns:'1fr 1fr', gap:'20px', padding:'20px', maxWidth:'1200px', margin:'0 auto', width:'100%', boxSizing:'border-box' }}>
          {/* Left — AI + Question */}
          <div style={{ display:'flex', flexDirection:'column', gap:'16px' }}>
            <div style={{ background:'rgba(255,255,255,0.05)', borderRadius:'16px', padding:'24px', border:'1px solid rgba(255,255,255,0.08)' }}>
              <div style={{ display:'flex', alignItems:'center', gap:'12px', marginBottom:'16px' }}>
                <div style={{ width:'48px', height:'48px', background:'linear-gradient(135deg,#6366f1,#8b5cf6)', borderRadius:'50%', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'22px' }}>🤖</div>
                <div>
                  <div style={{ color:'white', fontSize:'13px', fontWeight:'700' }}>Skillora AI Interviewer</div>
                  <div style={{ color:'#4ade80', fontSize:'11px' }}>● Asking question {videoCurrent+1}</div>
                </div>
              </div>
              <h3 style={{ color:'white', fontSize:'17px', fontWeight:'700', lineHeight:'1.6', margin:'0 0 16px' }}>Q{videoCurrent+1}. {q.question}</h3>
              <button onClick={() => speakQuestion(`Question ${videoCurrent+1}. ${q.question}`)}
                style={{ padding:'8px 16px', background:'rgba(99,102,241,0.2)', color:'#818cf8', border:'1px solid rgba(99,102,241,0.3)', borderRadius:'8px', cursor:'pointer', fontSize:'12px', fontFamily:'inherit', fontWeight:'600' }}>
                🔊 Repeat Question
              </button>
            </div>

            <div style={{ background:'rgba(255,255,255,0.03)', borderRadius:'16px', padding:'20px', border:'1px solid rgba(255,255,255,0.08)', flex:1 }}>
              <div style={{ display:'flex', justifyContent:'space-between', marginBottom:'12px' }}>
                <span style={{ color:'rgba(255,255,255,0.5)', fontSize:'12px', fontWeight:'600' }}>🎤 YOUR ANSWER</span>
                <span style={{ color:isRecording?'#4ade80':'#94a3b8', fontSize:'12px' }}>{isRecording?'● Recording':'○ Stopped'}</span>
              </div>
              {/* Real-time confidence indicators */}
              {isRecording && (
                <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:'6px', marginBottom:'10px' }}>
                  <div style={{ background:'rgba(99,102,241,0.15)', borderRadius:'8px', padding:'6px', textAlign:'center' }}>
                    <div style={{ color:'#818cf8', fontSize:'14px', fontWeight:'700' }}>{confidenceData.wordCount || 0}</div>
                    <div style={{ color:'rgba(255,255,255,0.4)', fontSize:'9px' }}>Words</div>
                  </div>
                  <div style={{ background: (confidenceData.pauseCount||0) > 3 ? 'rgba(239,68,68,0.15)' : 'rgba(22,163,74,0.15)', borderRadius:'8px', padding:'6px', textAlign:'center' }}>
                    <div style={{ color: (confidenceData.pauseCount||0) > 3 ? '#fca5a5' : '#4ade80', fontSize:'14px', fontWeight:'700' }}>{confidenceData.pauseCount || 0}</div>
                    <div style={{ color:'rgba(255,255,255,0.4)', fontSize:'9px' }}>Pauses</div>
                  </div>
                  <div style={{ background: (confidenceData.fillerCount||0) > 2 ? 'rgba(245,158,11,0.15)' : 'rgba(22,163,74,0.15)', borderRadius:'8px', padding:'6px', textAlign:'center' }}>
                    <div style={{ color: (confidenceData.fillerCount||0) > 2 ? '#fde68a' : '#4ade80', fontSize:'14px', fontWeight:'700' }}>{confidenceData.fillerCount || 0}</div>
                    <div style={{ color:'rgba(255,255,255,0.4)', fontSize:'9px' }}>Fillers</div>
                  </div>
                </div>
              )}
              <textarea value={currentVideoAnswer} onChange={e => setCurrentVideoAnswer(e.target.value)}
                placeholder="Speak your answer — it will appear here automatically..."
                style={{ width:'100%', background:'transparent', border:'none', color:'white', fontSize:'14px', lineHeight:'1.7', resize:'none', outline:'none', minHeight:'120px', fontFamily:'inherit', boxSizing:'border-box' }}/>
            </div>

            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'10px' }}>
              <button onClick={() => startVideo(`Question ${videoCurrent+1}. ${q.question}`)}
                style={{ padding:'12px', background:'rgba(22,163,74,0.2)', color:'#4ade80', border:'1px solid rgba(22,163,74,0.3)', borderRadius:'10px', cursor:'pointer', fontSize:'13px', fontFamily:'inherit', fontWeight:'600' }}>🎥 Restart Camera</button>

              <button onClick={() => { goNextVideo(currentVideoAnswer); }}
                style={{ padding:'12px', background:'linear-gradient(135deg,#6366f1,#8b5cf6)', color:'white', border:'none', borderRadius:'10px', cursor:'pointer', fontSize:'13px', fontFamily:'inherit', fontWeight:'700' }}>
                {videoCurrent < videoQuestions.length-1 ? 'Next →' : '✓ Submit'}
              </button>
            </div>
          </div>

          {/* Right — Camera */}
          <div style={{ display:'flex', flexDirection:'column', gap:'16px' }}>
            <div style={{ background:'#000', borderRadius:'16px', overflow:'hidden', position:'relative', border:'2px solid rgba(99,102,241,0.5)', aspectRatio:'4/3' }}>
              <video 
                ref={(el) => { videoRef.current = el; if (el) attachStream(el); }}
                autoPlay muted playsInline 
                onLoadedMetadata={(e) => e.target.play()}
                style={{ width:'100%', height:'100%', objectFit:'cover', transform:'scaleX(-1)', display:'block' }}/>
              {!isRecording && (
                <div style={{ position:'absolute', inset:0, display:'flex', alignItems:'center', justifyContent:'center', flexDirection:'column', gap:'12px', background:'rgba(0,0,0,0.8)' }}>
                  <div style={{ fontSize:'48px' }}>📷</div>
                  <p style={{ color:'rgba(255,255,255,0.5)', fontSize:'14px', margin:0 }}>Starting camera...</p>
                </div>
              )}
              {isRecording && <div style={{ position:'absolute', top:'12px', right:'12px', background:'rgba(239,68,68,0.85)', color:'white', padding:'4px 12px', borderRadius:'20px', fontSize:'11px', fontWeight:'700' }}>🔴 LIVE</div>}
              <div style={{ position:'absolute', bottom:'12px', left:'12px', color:'rgba(255,255,255,0.7)', fontSize:'12px', background:'rgba(0,0,0,0.6)', padding:'4px 10px', borderRadius:'8px' }}>
                {candidateInfo?.name}
              </div>
            </div>

            <div style={{ display:'flex', gap:'3px', flexWrap:'wrap', justifyContent:'center' }}>
              {videoQuestions.map((_,i) => (
                <div key={i} style={{ width:'8px', height:'8px', borderRadius:'50%', background:i<videoCurrent?'#6366f1':i===videoCurrent?'white':'rgba(255,255,255,0.2)' }}/>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (phase === 'submitting') return (
    <div style={{ ...base, display:'flex', alignItems:'center', justifyContent:'center' }}>
      <div style={{ textAlign:'center' }}>
        <div style={{ width:'56px', height:'56px', border:'3px solid rgba(99,102,241,0.3)', borderTop:'3px solid #6366f1', borderRadius:'50%', animation:'spin 1s linear infinite', margin:'0 auto 20px' }}/>
        <h3 style={{ color:'white', fontSize:'20px', fontWeight:'800', margin:'0 0 8px' }}>🤖 AI is analyzing your interview...</h3>
        <p style={{ color:'rgba(255,255,255,0.4)', fontSize:'14px' }}>Generating combined report...</p>
        <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
      </div>
    </div>
  );

  if (phase === 'result' && finalResult) {
    const recColor = (r) => r==='Recommended'?'#16a34a':r==='Conditional'?'#f59e0b':'#dc2626';
    const recBg = (r) => r==='Recommended'?'#dcfce7':r==='Conditional'?'#fef9c3':'#fee2e2';
    return (
      <div style={{ ...base, padding:'20px', display:'flex', alignItems:'center', justifyContent:'center' }}>
        <div style={{ background:'white', borderRadius:'24px', padding:'40px', maxWidth:'660px', width:'100%', boxShadow:'0 40px 80px rgba(0,0,0,0.4)' }}>
          <div style={{ textAlign:'center', marginBottom:'32px' }}>
            <div style={{ fontSize:'56px', marginBottom:'12px' }}>🎉</div>
            <h2 style={{ color:'#1e293b', fontSize:'26px', fontWeight:'900', margin:'0 0 6px' }}>Interview Complete!</h2>
            <p style={{ color:'#64748b', fontSize:'14px', margin:0 }}>Both phases completed — AI report ready</p>
          </div>

          {/* Final Score */}
          <div style={{ background:'linear-gradient(135deg,#0f172a,#1e1b4b)', borderRadius:'16px', padding:'28px', marginBottom:'20px', textAlign:'center' }}>
            <div style={{ color:'rgba(255,255,255,0.5)', fontSize:'12px', fontWeight:'600', marginBottom:'4px', letterSpacing:'1px' }}>FINAL COMBINED SCORE</div>
            <div style={{ color:'white', fontSize:'64px', fontWeight:'900', marginBottom:'4px' }}>{finalResult.finalScore}</div>
            <div style={{ color:'rgba(255,255,255,0.5)', fontSize:'14px' }}>out of 10</div>
            {finalResult.hiringRecommendation && (
              <div style={{ marginTop:'12px' }}>
                <span style={{ padding:'6px 16px', background:recBg(finalResult.hiringRecommendation), color:recColor(finalResult.hiringRecommendation), borderRadius:'20px', fontSize:'13px', fontWeight:'700' }}>
                  {finalResult.hiringRecommendation==='Recommended'?'✅':finalResult.hiringRecommendation==='Conditional'?'⚠️':'❌'} {finalResult.hiringRecommendation}
                </span>
              </div>
            )}
          </div>

          {/* Phase scores */}
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'12px', marginBottom:'16px' }}>
            <div style={{ background:'#f5f3ff', borderRadius:'12px', padding:'16px', border:'1px solid #c4b5fd', textAlign:'center' }}>
              <div style={{ color:'#6366f1', fontSize:'11px', fontWeight:'600', marginBottom:'4px' }}>PHASE 1 — TEXT</div>
              <div style={{ color:'#6366f1', fontSize:'28px', fontWeight:'900' }}>{textScores?.overallScore}/10</div>
            </div>
            <div style={{ background:'#0f172a', borderRadius:'12px', padding:'16px', border:'1px solid rgba(99,102,241,0.3)', textAlign:'center' }}>
              <div style={{ color:'#818cf8', fontSize:'11px', fontWeight:'600', marginBottom:'4px' }}>PHASE 2 — VIDEO</div>
              <div style={{ color:'white', fontSize:'28px', fontWeight:'900' }}>{finalResult.videoScore}/10</div>
            </div>
          </div>

          {/* Score breakdown */}
          <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:'10px', marginBottom:'16px' }}>
            {[['💻 Technical', finalResult.technicalScore],['💬 Communication', finalResult.communicationScore],['💪 Confidence', finalResult.confidenceScore]].map(([label,score]) => (
              <div key={label} style={{ background:'#f8fafc', borderRadius:'12px', padding:'14px', textAlign:'center', border:'1px solid #e2e8f0' }}>
                <div style={{ color:scoreColor(score), fontSize:'24px', fontWeight:'800' }}>{score||'—'}</div>
                <div style={{ color:'#64748b', fontSize:'11px', marginTop:'3px' }}>{label}</div>
                <div style={{ color:scoreColor(score), fontSize:'10px', fontWeight:'600' }}>{scoreLabel(score)}</div>
              </div>
            ))}
          </div>

          {finalResult.summary && (
            <div style={{ background:'#f0fdf4', borderRadius:'12px', padding:'16px', marginBottom:'16px', border:'1px solid #86efac' }}>
              <h4 style={{ color:'#166534', fontSize:'13px', fontWeight:'700', margin:'0 0 6px' }}>📋 AI Assessment</h4>
              <p style={{ color:'#166534', fontSize:'13px', margin:0, lineHeight:'1.6' }}>{finalResult.summary}</p>
            </div>
          )}

          <div style={{ background:'#f5f3ff', borderRadius:'12px', padding:'14px', textAlign:'center', border:'1px solid #c4b5fd' }}>
            <p style={{ color:'#6366f1', fontSize:'13px', margin:0, fontWeight:'600' }}>✅ Complete report submitted to Skillora team!</p>
          </div>
        </div>
      </div>
    );
  }

  return null;
}
